import { useMemo, useState } from "react";
import {
  currentRsvpSummary,
  normalizeSurveyRows,
  pollNeedsResponse,
  surveyTypeLabel,
} from "../../../utils/clientSessionUtils";
import AttendancePollButtons from "./AttendancePollButtons";
import s from "./clientApp.module.css";

function sessionFromSurveyRow(row) {
  if (!row?.session) return null;
  return {
    id: row.session.id,
    session_id: row.session.id,
    start_datetime: row.session.start_datetime,
    end_datetime: row.session.end_datetime,
    session_kind: "training",
    source: "training_session",
    group: row.session.group,
    booking_mode: row.session.booking_mode,
    rsvp_capacity: row.session.rsvp_capacity ?? row.session.group?.rsvp_capacity,
    rsvp_coming_count: row.session.rsvp_coming_count,
    rsvp_spots_left: row.session.rsvp_spots_left,
    rsvp_full: row.session.rsvp_full,
    client_rsvp: row.session.client_rsvp,
  };
}

function AttendanceSurveyCard({ row, onDone }) {
  const [phase, setPhase] = useState("choose");
  const session = sessionFromSurveyRow(row);
  const sessionStarted =
    session?.start_datetime &&
    new Date(session.start_datetime).getTime() <= Date.now();
  const answered = Boolean(row.responded_at) || Boolean(currentRsvpSummary(session, row));
  const groupLabel = row.poll?.title || row.session?.group?.name || "Опрос";

  if (sessionStarted && !answered) return null;

  return (
    <article
      className={`${s.homeSurveyCard} ${s.homeSurveyCardAttendance} ${
        answered ? s.homeSurveyCardAnswered : ""
      }`}
    >
      <header className={s.homeSurveyCardHead}>
        <span className={s.homeSurveyTypeBadge}>
          {surveyTypeLabel("attendance")}
        </span>
      </header>
      <h3 className={s.homeSurveyTitle}>{groupLabel}</h3>
      {row.poll?.description ? (
        <p className={s.homeSurveyMeta}>{row.poll.description}</p>
      ) : null}
      <AttendancePollButtons
        session={session}
        pollRow={row}
        allowDirect={Boolean(session)}
        variant="sheet"
        question={row.poll?.questions?.[0]?.text || row.question || "Ваш ответ"}
        onPhaseChange={setPhase}
        onDone={onDone}
      />
      {answered && phase === "answered" ? (
        <p className={s.homeSurveyAnsweredHint}>Отвечено</p>
      ) : null}
    </article>
  );
}

function FeedbackSurveyCard({ row, onDone }) {
  const answered = Boolean(row.responded_at);
  const type = row.survey_type || row.poll?.poll_type;

  return (
    <article
      className={`${s.homeSurveyCard} ${s.homeSurveyCardFeedback} ${
        answered ? s.homeSurveyCardAnswered : ""
      }`}
    >
      <header className={s.homeSurveyCardHead}>
        <span className={s.homeSurveyTypeBadge}>{surveyTypeLabel(type)}</span>
      </header>
      <h3 className={s.homeSurveyTitle}>{row.poll?.title || "Опрос"}</h3>
      {row.poll?.description ? (
        <p className={s.homeSurveyMeta}>{row.poll.description}</p>
      ) : null}
      {!answered && pollNeedsResponse(row) ? (
        <AttendancePollButtons
          pollRow={row}
          allowDirect={false}
          variant="sheet"
          question={row.poll?.questions?.[0]?.text || row.question}
          onDone={onDone}
        />
      ) : (
        <p className={s.homeSurveyAnsweredHint}>Спасибо за ответ</p>
      )}
    </article>
  );
}

export default function ClientSurveyFeed({ sections, onDone }) {
  const attendance = useMemo(
    () => normalizeSurveyRows(sections?.attendance || []),
    [sections],
  );
  const feedback = useMemo(
    () => normalizeSurveyRows(sections?.feedback || []),
    [sections],
  );
  const custom = useMemo(
    () => normalizeSurveyRows(sections?.custom || []),
    [sections],
  );

  const hasAny =
    attendance.length > 0 || feedback.length > 0 || custom.length > 0;
  if (!hasAny) return null;

  return (
    <section className={s.homeSurveyZone} aria-label="Опросы и подтверждения">
      <h2 className={s.homeSurveyZoneTitle}>Требует ответа</h2>
      <div className={s.homeSurveyList}>
        {attendance.map((row) => (
          <AttendanceSurveyCard
            key={`att-${row.poll_id}-${row.dispatch_id || row.id}`}
            row={row}
            onDone={onDone}
          />
        ))}
        {feedback.map((row) => (
          <FeedbackSurveyCard
            key={`fb-${row.id}`}
            row={row}
            onDone={onDone}
          />
        ))}
        {custom.map((row) => (
          <FeedbackSurveyCard
            key={`cu-${row.id}`}
            row={row}
            onDone={onDone}
          />
        ))}
      </div>
    </section>
  );
}
